import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import TransitionLink from '../../layout/TransitionLink';
import AnimatedWave from '../canvas/AnimatedWave';

interface OpFooterProps {
  darkMode: boolean;
}

const socialLinks = [
  { name: 'GitHub', href: 'https://github.com/tkkalkhaled' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/in/talal-alkhaled-a0779b321/' },
  { name: 'Instagram', href: 'https://www.instagram.com/talalal5aled' },
];

const OpFooter: React.FC<OpFooterProps> = ({ darkMode }) => {
  const { t } = useTranslation();
  const ink = darkMode ? '240, 238, 232' : '29, 27, 24';

  const footerLinks: { title: string; links: { name: string; to: string; badge?: string }[] }[] = [
    {
      title: t('op.footer.colExplore'),
      links: [
        { name: t('nav.portfolio'), to: '/projects' },
        { name: t('nav.blog'), to: '/blog' },
        { name: t('nav.about'), to: '/about' },
        { name: t('nav.chat'), to: '/chat', badge: 'GPT-5.4' },
        { name: t('nav.contact'), to: '/contact' },
      ],
    },
    {
      title: t('op.footer.colProjects'),
      links: [
        { name: 'NOTEorious', to: '/project/modern-notes-app' },
        { name: t('op.footer.aiChat'), to: '/project/ai-powered-projects-chat-system' },
        { name: 'LexifAI', to: '/project/lexifai-ocr-chatbot' },
        { name: t('op.footer.securityProject'), to: '/project/website-security-implementation' },
      ],
    },
    {
      title: t('op.footer.colWriting'),
      links: [
        { name: t('op.footer.blogReact'), to: '/blog/react-performance-optimization' },
        { name: t('op.footer.blogSecurity'), to: '/blog/web-security-implementation' },
        { name: t('op.footer.blogAi'), to: '/blog/ai-integration-guide' },
      ],
    },
    {
      title: t('op.footer.colLegal'),
      links: [
        { name: t('op.footer.privacy'), to: '/privacy' },
        { name: t('op.footer.terms'), to: '/terms' },
      ],
    },
  ];

  return (
    <footer className="relative border-t border-foreground/10">
      {/* Animated wave background — covers the full footer */}
      <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
        <AnimatedWave ink={ink} />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Main Footer */}
        <div className="py-16 lg:py-24">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-12 lg:gap-8">
            {/* Brand Column */}
            <div className="col-span-2">
              <TransitionLink to="/" className="inline-flex items-center gap-2 mb-6">
                <span className="text-2xl font-display">Talal Alkhaled</span>
              </TransitionLink>

              <p className="text-muted-foreground leading-relaxed mb-8 max-w-xs">
                {t('op.footer.tagline')}
              </p>

              {/* Social Links */}
              <div className="flex gap-6">
                {socialLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </a>
                ))}
              </div>
            </div>

            {/* Link Columns */}
            {footerLinks.map((column) => (
              <div key={column.title}>
                <h3 className="text-sm font-medium mb-6">{column.title}</h3>
                <ul className="space-y-4">
                  {column.links.map((link) => (
                    <li key={link.name}>
                      <TransitionLink
                        to={link.to}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                      >
                        {link.name}
                        {link.badge && (
                          <span dir="ltr" className="text-xs px-2 py-0.5 bg-foreground text-background rounded-full whitespace-nowrap">
                            {link.badge}
                          </span>
                        )}
                      </TransitionLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 border-t border-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Talal Alkhaled. {t('op.footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default OpFooter;
